import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { contactInfo, testimonials } from '../data/mock';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setIsSubmitting(true);
    
    // Simulamos envío (mock)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Mensaje enviado correctamente. Te responderemos pronto.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  return (
    <section className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 border border-violet-500/30 rounded-full mb-6"
          >
            <MessageSquare className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-mono tracking-wider uppercase">Contáctanos</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Contacto
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Estamos para ayudarte. Escríbenos o visítanos en nuestra tienda
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Info Cards */}
            <div className="space-y-4 mb-8">
              <motion.div variants={itemVariants}>
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="block">
                  <Card className="bg-white/5 border-violet-900/30 hover:border-violet-500/50 hover:bg-violet-600/10 transition-all cursor-pointer">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Teléfono / WhatsApp</p>
                        <p className="text-white font-medium hover:text-violet-400 transition-colors">{contactInfo.phone}</p>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </motion.div>

              <motion.div variants={itemVariants}>
                <a href={`mailto:${contactInfo.email}`} className="block">
                  <Card className="bg-white/5 border-violet-900/30 hover:border-violet-500/50 hover:bg-violet-600/10 transition-all cursor-pointer">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Email</p>
                        <p className="text-white font-medium hover:text-violet-400 transition-colors">{contactInfo.email}</p>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </motion.div>

              <motion.div variants={itemVariants}>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="bg-white/5 border-violet-900/30 hover:border-violet-500/50 hover:bg-violet-600/10 transition-all cursor-pointer">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Dirección</p>
                        <p className="text-white font-medium hover:text-violet-400 transition-colors">{contactInfo.address}</p>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card className="bg-white/5 border-violet-900/30">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Horarios</p>
                      <p className="text-white font-medium text-sm">{contactInfo.hours.weekdays}</p>
                      {contactInfo.hours.sunday && (
                        <p className="text-gray-400 text-sm">{contactInfo.hours.sunday}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Testimonials */}
            <motion.div variants={itemVariants}>
              <h3 className="text-lg font-semibold text-white mb-4">Lo que dicen nuestros clientes</h3>
              <div className="space-y-3">
                {testimonials.map((testimonial) => (
                  <Card key={testimonial.id} className="bg-violet-600/10 border-violet-500/20">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-violet-400 fill-current" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-gray-300 text-sm italic">"{testimonial.text}"</p>
                      <p className="text-violet-400 text-xs mt-2 font-mono">- {testimonial.name}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-white/5 border-violet-900/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-violet-400" />
                  Envíanos un Mensaje
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Responderemos a la brevedad posible
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Nombre *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Tu nombre"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600 focus:border-violet-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600 focus:border-violet-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Asunto</label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Asunto de tu consulta"
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600 focus:border-violet-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Mensaje *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Escribe tu mensaje aquí..."
                      rows={5}
                      className="bg-black/50 border-violet-900/50 text-white placeholder:text-gray-600 focus:border-violet-500 resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full font-mono text-xs tracking-wider uppercase flex items-center justify-center gap-2 py-6"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Enviar Mensaje
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
